import type { RMResourceDoc } from "resource-manager";

import { getIdToken } from "@/services/Auth";
import { getBaseApiUrl, getDefaultHeaders, request } from "@/utils/CreativeIntelligenceApi";
import { API_ROUTES } from "@/constants";

export const RESOURCE_MANAGER_STATUS = {
    AVAILABLE: "AVAILABLE",
    IN_PROGRESS: "IN_PROGRESS",
    FAILED: "FAILED",
    BUDGET_LIMIT_HIT: "BUDGET_LIMIT_HIT"
} as const;

export const RESOURCE_MANAGER_TYPES = {
    PLAINLY_CREATE_PROJECT: "PLAINLY_CREATE_PROJECT",
    PLAINLY_RENDER: "PLAINLY_RENDER",
    PLAINLY_BATCH_RENDER: "PLAINLY_BATCH_RENDER",
    ADOBE: "ADOBE"
} as const;

type ResourceManagerInProgressResponse = {
    resourceId: string;
    status: "IN_PROGRESS";
    percentage: number;
    output?: {
        inProgress: unknown;
    };
};

export type ResourceManagerFailedResponse = {
    resourceId: string;
    status: "FAILED";
    percentage: number;
    output: {
        fail: Error | string;
    };
};

export type ResourceManagerSuccessResponse<T> = {
    resourceId: string;
    status: "AVAILABLE";
    percentage: number;
    output: {
        success: T;
    };
};

export type ResourceManagerBudgetLimitResponse = {
    resourceId: string;
    status: "BUDGET_LIMIT_HIT";
    percentage: number;
    output?: {
        fail: string;
    };
};

export type ResourceManagerResponse<T> =
    | ResourceManagerInProgressResponse
    | ResourceManagerFailedResponse
    | ResourceManagerSuccessResponse<T>
    | ResourceManagerBudgetLimitResponse;

export async function resourceManagerRequest<T, B = unknown>({
    id,
    body,
    signal
}: {
    id: string;
    body?: B;
    signal?: AbortSignal;
}) {
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.RESOURCE_MANAGER_REQUEST}/${id}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<ResourceManagerResponse<T>, B>(url, headers, "POST", body, { signal });
}

export function getResourceStatus(resource: RMResourceDoc<unknown, unknown>) {
    if (resource.output?.fail) {
        return RESOURCE_MANAGER_STATUS.FAILED;
    }

    if (resource.output?.success) {
        return RESOURCE_MANAGER_STATUS.AVAILABLE;
    }

    return RESOURCE_MANAGER_STATUS.IN_PROGRESS;
}

export async function getResourceManagerResourceById<T = unknown, I = unknown>({
    resourceId
}: {
    resourceId: string;
}): Promise<{ data: RMResourceDoc<T, I> }> {
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.resourceManagerById(resourceId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<RMResourceDoc<T, I>>(url, headers, "GET");
}

export async function resourceListRequest<T = unknown, I = unknown>({ id }: { id: string }) {
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.resourceManagerList(id)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<{ items: RMResourceDoc<T, I>[]; nextPageToken: string }>(url, headers, "GET");
}
